import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, RotateCcw, Check, ChevronDown, Loader2, Droplet, Sparkles, ShieldCheck, Info, Palette, Layers, Bookmark, Mail } from "lucide-react";
import { Reveal } from "./components";
import { QuoteCTA } from "./pages";
import { useAuth } from "./auth";
import { EmailQuizModal } from "./portal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Sheen reflectance illustration (CSS only) --------------------------
function sheenStyle(level, base = "#cfc7ba") {
  const alpha = 0.05 + level * 0.235; // 0.05 (matte) -> 0.99 (high gloss)
  const band = 16 - level * 3.2;      // wide, diffuse (matte) -> narrow, sharp (gloss)
  const start = 42 - band;
  const end = 54 + band;
  const floor = level >= 3
    ? `, linear-gradient(0deg, rgba(255,255,255,${(level - 2) * 0.12}) 0%, transparent 24%)`
    : "";
  return {
    background:
      `linear-gradient(118deg, transparent ${start}%, rgba(255,255,255,${alpha}) 48%, rgba(255,255,255,${alpha * 0.35}) 52%, transparent ${end}%)` +
      `, radial-gradient(120% 90% at 18% 12%, rgba(255,255,255,${0.10 + level * 0.06}) 0%, transparent 45%)` +
      floor +
      `, ${base}`,
  };
}

function SheenSwatch({ level, label, base, className = "" }) {
  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="w-full h-20 md:h-24 rounded-md border border-black/10 shadow-inner" style={sheenStyle(level, base)} />
      {label && <span className="text-[10px] md:text-xs uppercase tracking-wider text-ink/60 mt-2 text-center">{label}</span>}
    </div>
  );
}

const SHEENS = [
  { key: "matte", level: 0, name: "Flat / Matte", tagline: "The softest look with very little reflection.",
    appearance: "A velvety, chalk-soft surface that absorbs light and feels calm and expensive.",
    durability: "Low", clean: "Spot-clean only; not scrub-friendly.",
    best: ["Ceilings", "Low-traffic bedrooms", "Formal living rooms", "Hiding wall imperfections"],
    drawback: "The least washable finish; marks and scuffs are hard to remove without touch-ups.",
    photo: "https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "Serene bedrooms and ceilings where a soft, glare-free look matters most." },
  { key: "eggshell", level: 1, name: "Eggshell", tagline: "Soft, low sheen with improved durability.",
    appearance: "A gentle, low-luster glow, like the surface of an eggshell, that stays understated.",
    durability: "Moderate", clean: "Wipes lightly with a damp cloth.",
    best: ["Most living rooms", "Dining rooms", "Hallways", "Bedrooms"],
    drawback: "Cleans better than matte but can still burnish if scrubbed too hard.",
    photo: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "The designer default for everyday living rooms and dining rooms." },
  { key: "satin", level: 2, name: "Satin", tagline: "Smooth finish that's easy to clean.",
    appearance: "A refined, pearl-like sheen that reads polished without looking shiny.",
    durability: "Good", clean: "Cleans easily and often with a damp cloth.",
    best: ["Kitchens", "Bathrooms", "Children's rooms", "Higher-traffic spaces"],
    drawback: "Its slight sheen can reveal roller marks and wall flaws in raking light.",
    photo: "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "Kitchens, baths and busy rooms that need to wipe clean." },
  { key: "semi-gloss", level: 3, name: "Semi-Gloss", tagline: "Noticeable sheen with excellent durability.",
    appearance: "A crisp, light-reflecting shine that frames a room and reads tailored.",
    durability: "Excellent", clean: "Scrub-friendly and moisture-resistant.",
    best: ["Trim", "Doors", "Cabinets", "Bathrooms & laundry rooms"],
    drawback: "The higher shine highlights any surface imperfection, so prep must be excellent.",
    photo: "https://images.pexels.com/photos/259962/pexels-photo-259962.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "Trim, doors and cabinetry that take contact and need to look crisp." },
  { key: "high-gloss", level: 4, name: "High Gloss", tagline: "Highest shine and easiest to wipe clean.",
    appearance: "An almost glass-like, lacquered surface that makes a bold, luxurious statement.",
    durability: "Maximum", clean: "Wipes clean effortlessly.",
    best: ["Specialty accents", "Furniture", "Statement doors", "Decorative applications"],
    drawback: "Unforgiving of flaws; requires meticulous prep and is often too reflective for full walls.",
    photo: "https://images.pexels.com/photos/1866149/pexels-photo-1866149.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "Furniture and decorative accents where drama and shine are the goal." },
];

const DUR_MAP = { Low: 1, Moderate: 2, Good: 3, Excellent: 4, Maximum: 5 };

function DurabilityMeter({ label }) {
  const filled = DUR_MAP[label] || 0;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <span key={i} className={`w-4 h-1.5 rounded-full ${i <= filled ? "bg-gold" : "bg-black/10"}`} />)}</span>
      <span className="text-xs text-ink/60">{label}</span>
    </span>
  );
}

function SheenCard({ s }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`sheen-card-${s.key}`} className="border border-black/10 bg-white">
      <button type="button" data-testid={`sheen-toggle-${s.key}`} onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-4 p-5 text-left">
        <div className="w-24 shrink-0 h-14 rounded-md border border-black/10" style={sheenStyle(s.level)} />
        <div className="flex-1">
          <p className="font-serif text-xl leading-tight">{s.name}</p>
          <p className="text-sm text-ink/55 mt-0.5">{s.tagline}</p>
        </div>
        <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`sheen-content-${s.key}`} className="px-5 pb-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <p className="flex items-start gap-2"><Sparkles size={15} className="text-gold mt-0.5 shrink-0" /><span><span className="font-medium">Appearance:</span> {s.appearance}</span></p>
            <p className="flex items-start gap-2"><ShieldCheck size={15} className="text-gold mt-0.5 shrink-0" /><span className="flex flex-wrap items-center gap-2"><span className="font-medium">Durability:</span> <DurabilityMeter label={s.durability} /></span></p>
            <p className="flex items-start gap-2"><Droplet size={15} className="text-gold mt-0.5 shrink-0" /><span><span className="font-medium">Ease of cleaning:</span> {s.clean}</span></p>
            <div className="flex items-start gap-2"><Check size={15} className="text-gold mt-0.5 shrink-0" /><div><span className="font-medium">Best uses:</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">{s.best.map((b) => <span key={b} className="text-[11px] border border-black/15 px-2 py-0.5">{b}</span>)}</div></div></div>
            <p className="flex items-start gap-2 text-ink/70"><Info size={15} className="text-ink/40 mt-0.5 shrink-0" /><span><span className="font-medium text-ink">Drawbacks:</span> {s.drawback}</span></p>
          </div>
          <figure>
            <img src={s.photo} alt={`${s.name} typical use`} className="w-full h-52 object-cover rounded-md" loading="lazy" />
            <figcaption className="text-xs text-ink/55 mt-2 italic">{s.caption} <span className="not-italic">— photo illustrates typical application, not the exact level of sheen.</span></figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}

// ---- Quiz ---------------------------------------------------------------
const opt = (value, label, desc) => ({ value, label, desc });
const QUESTIONS = [
  { id: "surface", title: "What are you painting?", subtitle: "The surface is the biggest driver of the right finish.",
    options: [opt("walls", "Walls"), opt("ceiling", "Ceiling"), opt("trim", "Trim"), opt("doors", "Doors"), opt("cabinets", "Cabinets"), opt("exterior", "Exterior"), opt("furniture", "Furniture")] },
  { id: "room", title: "Which room is it?", subtitle: "Optional, but it sharpens the recommendation.", optional: true,
    options: [opt("living", "Living Room"), opt("bedroom", "Bedroom"), opt("kitchen", "Kitchen"), opt("bathroom", "Bathroom"), opt("dining", "Dining Room"), opt("hallway", "Hallway"), opt("kids_room", "Children's Room"), opt("laundry", "Laundry / Mudroom"), opt("office", "Home Office"), opt("", "Other")] },
  { id: "kids_pets", title: "Do you have children or pets?", subtitle: "Little hands and paws mean more cleaning.",
    options: [opt("yes", "Yes"), opt("no", "No")] },
  { id: "high_traffic", title: "Is this a high-traffic area?", subtitle: "Busy spaces take more wear.",
    options: [opt("yes", "Yes, it gets a lot of use"), opt("no", "No, it's fairly quiet")] },
  { id: "moisture", title: "Is moisture common in this room?", subtitle: "Think steam, splashes and humidity.",
    options: [opt("yes", "Yes"), opt("no", "No")] },
  { id: "preference", title: "Which matters more to you?", subtitle: "There's no wrong answer, it just tunes the finish.",
    options: [opt("soft", "A softer, designer look"), opt("durable", "Maximum durability")] },
  { id: "wall_condition", title: "Are the surfaces smooth or imperfect?", subtitle: "Higher sheens reveal flaws; lower sheens hide them.",
    options: [opt("smooth", "Smooth & well-prepped"), opt("imperfections", "Some imperfections")] },
];

export function SheenQuiz() {
  const [params] = useSearchParams();
  const prefill = useMemo(() => {
    const p = {};
    if (params.get("surface")) p.surface = params.get("surface");
    if (params.get("room")) p.room = params.get("room");
    return p;
  }, [params]);

  const [phase, setPhase] = useState("learn"); // learn | quiz | loading | results
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(prefill);
  const [result, setResult] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const { user, http } = useAuth();

  const saveSheen = async () => {
    if (!user) { window.location.href = "/signup"; return; }
    try {
      await http.post("/portal/quiz-results", { kind: "sheen", answers, results: result, title: `Sheen: ${result?.recommended?.sheen || ""}` });
      setSavedFlag(true); toast.success("Saved to your portal");
    } catch { toast.error("Could not save"); }
  };

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];
  const selected = answers[q?.id];

  const choose = (value) => {
    setAnswers((a) => ({ ...a, [q.id]: value }));
    if (step < total - 1) setTimeout(() => setStep((s) => s + 1), 180);
  };
  const next = () => { if (step < total - 1) setStep((s) => s + 1); else submit(); };
  const back = () => { if (step > 0) setStep((s) => s - 1); else setPhase("learn"); };

  const submit = async () => {
    setPhase("loading");
    try {
      const r = await axios.post(`${API}/sheen/recommend`, { answers });
      setResult(r.data); setPhase("results"); window.scrollTo(0, 0);
    } catch { toast.error("Something went wrong. Please try again."); setPhase("quiz"); }
  };
  const restart = () => { setAnswers(prefill); setStep(0); setResult(null); setPhase("learn"); window.scrollTo(0, 0); };
  const startQuiz = () => { setPhase("quiz"); setStep(0); document.getElementById("sheen-quiz-anchor")?.scrollIntoView({ behavior: "smooth" }); };

  const canProceed = q?.optional || selected !== undefined;
  const isLast = step === total - 1;

  // ---- RESULTS ----
  if (phase === "results" && result) {
    const rec = result.recommended, alt = result.alternate;
    const meta = (k) => SHEENS.find((s) => s.key === k) || {};
    return (
      <div data-testid="sheen-results-page" className="pt-32 bg-ivory min-h-screen">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <Reveal>
            <p className="eyebrow text-gold">Your Sheen Recommendation</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">We recommend {rec.name}</h1>
          </Reveal>

          <div className="grid lg:grid-cols-3 gap-6 mt-10">
            {/* Recommended */}
            <div data-testid="sheen-recommended" className="lg:col-span-2 bg-white border border-black/10 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.25)] p-7 md:p-9">
              <span className="text-[10px] font-bold uppercase tracking-widest bg-ink text-white px-3 py-1.5">Recommended</span>
              <div className="h-28 rounded-md border border-black/10 mt-5" style={sheenStyle(meta(rec.key).level ?? 1)} />
              <h2 className="font-serif text-3xl mt-5">{rec.name}</h2>
              <p className="text-ink/60 mt-1">{rec.one_liner}</p>
              <ul className="mt-5 space-y-2">
                {rec.reasons.map((r) => <li key={r} className="flex items-start gap-2 text-sm text-ink/80"><Check size={15} className="text-gold mt-0.5 shrink-0" /> {r}</li>)}
              </ul>
              <div className="mt-6 border-t border-black/10 pt-5">
                <p className="text-xs uppercase tracking-widest text-gold">Maintenance tips</p>
                <p className="text-sm text-ink/70 mt-2">{result.maintenance_tips}</p>
              </div>
            </div>

            {/* Alternate */}
            <div data-testid="sheen-alternate" className="bg-ink text-white p-7 md:p-9">
              <span className="text-[10px] font-bold uppercase tracking-widest bg-gold text-white px-3 py-1.5">Great Alternate</span>
              <div className="h-24 rounded-md border border-white/15 mt-5" style={sheenStyle(meta(alt.key).level ?? 1, "#3a3a38")} />
              <h2 className="font-serif text-2xl mt-5">{alt.name}</h2>
              <p className="text-white/60 text-sm mt-1">{alt.one_liner}</p>
              <p className="text-white/70 text-sm mt-4">{alt.role_blurb}</p>
              <p className="text-xs uppercase tracking-widest text-gold mt-6">Maintenance</p>
              <p className="text-white/70 text-sm mt-2">{alt.maintenance}</p>
            </div>
          </div>

          {/* Why not others */}
          <Reveal>
            <div className="mt-10 bg-white border border-black/10 p-7 md:p-9">
              <h3 className="font-serif text-2xl">Why not the other finishes?</h3>
              <div className="grid md:grid-cols-3 gap-5 mt-5">
                {result.why_not_others.map((w) => (
                  <div key={w.sheen} data-testid={`why-not-${w.sheen}`} className="border-l-2 border-black/10 pl-4">
                    <p className="font-medium">{w.name}</p>
                    <p className="text-sm text-ink/60 mt-1">{w.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* CTAs */}
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            <Link to="/color-quiz" data-testid="sheen-cta-colors" className="bg-white border border-black/10 p-6 hover:border-gold transition-colors group">
              <Palette size={22} className="text-gold" /><p className="font-serif text-xl mt-3">Find your color</p><p className="text-sm text-ink/60 mt-1">Take the Color Quiz for a matched paint color <ArrowRight size={13} className="inline group-hover:translate-x-1 transition-transform" /></p>
            </Link>
            <Link to="/palettes" data-testid="sheen-cta-palettes" className="bg-white border border-black/10 p-6 hover:border-gold transition-colors group">
              <Layers size={22} className="text-gold" /><p className="font-serif text-xl mt-3">Curated palettes</p><p className="text-sm text-ink/60 mt-1">Explore designer-curated color palettes <ArrowRight size={13} className="inline group-hover:translate-x-1 transition-transform" /></p>
            </Link>
            <Link to="/consult" data-testid="sheen-cta-consult" className="bg-white border border-black/10 p-6 hover:border-gold transition-colors group">
              <Droplet size={22} className="text-gold" /><p className="font-serif text-xl mt-3">1:1 Color Consult</p><p className="text-sm text-ink/60 mt-1">Book a private session with our designer <ArrowRight size={13} className="inline group-hover:translate-x-1 transition-transform" /></p>
            </Link>
          </div>

          <div className="flex flex-wrap gap-4 mt-10">
            <button onClick={saveSheen} data-testid="sheen-save-portal-btn" className="inline-flex items-center gap-2 bg-ink text-white overline px-8 py-4 hover:bg-gold transition-colors">
              <Bookmark size={15} /> {savedFlag ? "Saved to Portal" : user ? "Save to My Portal" : "Save to Portal (Sign up)"}
            </button>
            <button onClick={() => setEmailOpen(true)} data-testid="sheen-email-btn" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">
              <Mail size={15} /> Email my result
            </button>
            <button onClick={restart} data-testid="sheen-restart-btn" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors"><RotateCcw size={15} /> Retake the Quiz</button>
          </div>
          {result.disclaimer && <p data-testid="sheen-disclaimer" className="text-xs text-ink/45 mt-8 max-w-3xl leading-relaxed">{result.disclaimer}</p>}
        </div>
        <QuoteCTA />
        <EmailQuizModal open={emailOpen} onClose={() => setEmailOpen(false)} kind="sheen" results={result} />
      </div>
    );
  }

  // ---- LEARN + QUIZ ----
  return (
    <div data-testid="sheen-quiz-page" className="pt-32 bg-ivory min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <Reveal>
          <p className="eyebrow text-gold">Paint Sheen Guide</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">Which paint finish is right for you?</h1>
          <p className="text-muted-foreground mt-5 max-w-3xl leading-relaxed">Sheen is the amount of shine in a paint, and it changes everything: how a wall looks, how durable it is, how easily it cleans, and how much light it reflects. Learn the five finishes below, then take our quick quiz for a designer recommendation.</p>
        </Reveal>

        {/* Progression illustration */}
        <Reveal>
          <div data-testid="sheen-progression" className="bg-white border border-black/10 p-6 md:p-8 mt-10">
            <div className="flex items-center justify-between mb-4">
              <p className="overline text-gold">Matte</p>
              <p className="text-xs uppercase tracking-widest text-ink/40">Increasing sheen & light reflection</p>
              <p className="overline text-gold">High Gloss</p>
            </div>
            <div className="grid grid-cols-5 gap-2 md:gap-4">
              {SHEENS.map((s) => <SheenSwatch key={s.key} level={s.level} base="#cfc7ba" label={s.name} />)}
            </div>
          </div>
        </Reveal>

        {/* Educational cards */}
        <div className="space-y-3 mt-8">
          {SHEENS.map((s) => <SheenCard key={s.key} s={s} />)}
        </div>

        <div id="sheen-quiz-anchor" className="pt-8" />

        {phase === "learn" && (
          <Reveal>
            <div className="bg-ink text-white p-8 md:p-12 mt-4 text-center">
              <h2 className="font-serif text-3xl md:text-4xl">Ready for a recommendation?</h2>
              <p className="text-white/70 mt-3 max-w-xl mx-auto">Answer seven quick questions and we&apos;ll match you to the ideal finish, plus a great alternate.</p>
              <button onClick={startQuiz} data-testid="start-sheen-quiz-btn" className="inline-flex items-center gap-2 bg-gold text-white overline px-9 py-4 mt-7 hover:bg-gold-dark transition-colors">Start the Sheen Quiz <ArrowRight size={15} /></button>
            </div>
          </Reveal>
        )}

        {phase === "loading" && (
          <div className="py-24 text-center" data-testid="sheen-loading"><Loader2 className="animate-spin mx-auto text-gold" size={40} /><p className="font-serif text-2xl mt-6">Finding your finish…</p></div>
        )}

        {phase === "quiz" && q && (
          <div className="mt-4">
            <div className="flex justify-between text-xs uppercase tracking-[0.14em] text-ink/50 mb-2">
              <span>Question {step + 1} of {total}</span>
              <span data-testid="sheen-progress-pct">{Math.round(((step + 1) / total) * 100)}%</span>
            </div>
            <div className="h-1 bg-black/10 w-full overflow-hidden"><div className="h-full bg-gold transition-all duration-500" style={{ width: `${((step + 1) / total) * 100}%` }} /></div>

            <div key={q.id} className="mt-8 bg-white border border-black/10 p-6 sm:p-9">
              <h2 className="font-serif text-2xl md:text-3xl leading-snug">{q.title}</h2>
              {q.subtitle && <p className="text-sm text-ink/55 mt-2">{q.subtitle}</p>}
              <div className="grid sm:grid-cols-2 gap-3 mt-7">
                {q.options.map((o) => {
                  const active = selected === o.value;
                  return (
                    <button key={o.label} type="button" data-testid={`sheen-opt-${q.id}-${o.value || "none"}`} onClick={() => choose(o.value)}
                      className={`text-left border px-5 py-4 transition-colors ${active ? "border-ink bg-ink text-white" : "border-black/15 bg-white hover:border-gold"}`}>
                      <span className="flex items-center justify-between gap-2"><span className="font-medium">{o.label}</span>{active && <Check size={16} className="text-gold shrink-0" />}</span>
                      {o.desc && <span className={`block text-xs mt-1 ${active ? "text-white/60" : "text-ink/45"}`}>{o.desc}</span>}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-9">
                <button type="button" onClick={back} data-testid="sheen-back-btn" className="inline-flex items-center gap-2 overline px-6 py-3.5 text-ink hover:text-gold transition-colors"><ArrowLeft size={15} /> Back</button>
                <button type="button" onClick={next} disabled={!canProceed} data-testid="sheen-next-btn"
                  className={`inline-flex items-center gap-2 overline px-9 py-4 transition-colors ${canProceed ? "bg-ink text-white hover:bg-gold" : "bg-black/10 text-ink/40 cursor-not-allowed"}`}>
                  {isLast ? "See My Finish" : "Next"} <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
